import { GAME_COLORS } from '../config/colors';
import { rgba, lightenCSS as lighten, darkenCSS as darken } from '../utils/color';

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// ── Main generator ───────────────────────────────────────────────────────────

export function generateSprites(textures: Phaser.Textures.TextureManager): void {
  generateCubeTextures(textures);
  generateSnakeCubeTextures(textures);
  generateAttackerTextures(textures);
  generateGlowTextures(textures);
  generateMysteryAttackerTexture(textures);
  generateMysteryGlowTexture(textures);
  generateProjectileTextures(textures);
  generateParticleTexture(textures);
  generateConveyorDot(textures);
  generateSlotTexture(textures);
}

// ── Bevel helper — draws 3D edge highlights/shadows inside a rounded rect ────

function drawBevel(
  ctx: CanvasRenderingContext2D,
  hex: number,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.save();
  roundedRect(ctx, x, y, w, h, r);
  ctx.clip();

  const bevel = 3; // bevel strip width

  // Top highlight — light strip fading down
  const topGrad = ctx.createLinearGradient(0, y, 0, y + bevel * 2);
  topGrad.addColorStop(0, lighten(hex, 50));
  topGrad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(x, y, w, bevel * 2);

  // Left highlight — light strip fading right
  const leftGrad = ctx.createLinearGradient(x, 0, x + bevel * 2, 0);
  leftGrad.addColorStop(0, lighten(hex, 35));
  leftGrad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(x, y, bevel * 2, h);

  // Bottom shadow — dark strip fading up
  const botGrad = ctx.createLinearGradient(0, y + h, 0, y + h - bevel * 2);
  botGrad.addColorStop(0, darken(hex, 40));
  botGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = botGrad;
  ctx.fillRect(x, y + h - bevel * 2, w, bevel * 2);

  // Right shadow — dark strip fading left
  const rightGrad = ctx.createLinearGradient(x + w, 0, x + w - bevel * 2, 0);
  rightGrad.addColorStop(0, darken(hex, 30));
  rightGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = rightGrad;
  ctx.fillRect(x + w - bevel * 2, y, bevel * 2, h);

  ctx.restore();
}

// ── Cube: compact rounded rect optimized for ~16px display ────────────────────

function generateCubeTextures(textures: Phaser.Textures.TextureManager): void {
  const size = 32;
  const r = 6;

  for (let i = 0; i < GAME_COLORS.length; i++) {
    const hex = GAME_COLORS[i].hex;
    const canvas = textures.createCanvas(`cube-${i}`, size, size)!;
    const ctx = canvas.getContext();

    // Body gradient (simplified — no shadow at small sizes)
    roundedRect(ctx, 2, 2, size - 4, size - 4, r);
    const grad = ctx.createLinearGradient(0, 0, 0, size);
    grad.addColorStop(0, lighten(hex, 30));
    grad.addColorStop(0.5, rgba(hex));
    grad.addColorStop(1, darken(hex, 20));
    ctx.fillStyle = grad;
    ctx.fill();

    // 3D bevel edges (highlight top/left, shadow bottom/right)
    drawBevel(ctx, hex, 2, 2, size - 4, size - 4, r);

    // Subtle bright border for definition at small sizes
    roundedRect(ctx, 3, 3, size - 6, size - 6, r);
    ctx.strokeStyle = lighten(hex, 20);
    ctx.lineWidth = 1.6;
    ctx.stroke();

    canvas.refresh();
  }
}

// ── Snake cube: rounded rect with diagonal stripe pattern ──────────────────

function generateSnakeCubeTextures(textures: Phaser.Textures.TextureManager): void {
  const size = 32;
  const r = 6;

  for (let i = 0; i < GAME_COLORS.length; i++) {
    const hex = GAME_COLORS[i].hex;
    const canvas = textures.createCanvas(`snake-cube-${i}`, size, size)!;
    const ctx = canvas.getContext();

    // Body gradient (same as regular cube)
    roundedRect(ctx, 2, 2, size - 4, size - 4, r);
    const grad = ctx.createLinearGradient(0, 0, 0, size);
    grad.addColorStop(0, lighten(hex, 30));
    grad.addColorStop(0.5, rgba(hex));
    grad.addColorStop(1, darken(hex, 20));
    ctx.fillStyle = grad;
    ctx.fill();

    // 3D bevel edges (highlight top/left, shadow bottom/right)
    drawBevel(ctx, hex, 2, 2, size - 4, size - 4, r);

    // Diagonal stripe overlay for snake distinction
    ctx.save();
    roundedRect(ctx, 2, 2, size - 4, size - 4, r);
    ctx.clip();
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 3;
    for (let d = -size; d < size * 2; d += 8) {
      ctx.beginPath();
      ctx.moveTo(d, 0);
      ctx.lineTo(d + size, size);
      ctx.stroke();
    }
    ctx.restore();

    // Border
    roundedRect(ctx, 3, 3, size - 6, size - 6, r);
    ctx.strokeStyle = lighten(hex, 35);
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }
}

// ── Attacker: glossy orb with radial gradient + highlight ────────────────────


// ── Parameterized drawing helpers ──────────────────────────────────────────

interface AttackerOrbOpts {
  lightenBody?: number;
  glossCenter?: number;
  glossMid?: number;
  rimLighten?: number;
}

function drawAttackerOrb(
  textures: Phaser.Textures.TextureManager,
  key: string,
  hex: number,
  opts?: AttackerOrbOpts,
): void {
  const size = 104;
  const lightenBody = opts?.lightenBody ?? 40;
  const glossCenter = opts?.glossCenter ?? 0.55;
  const glossMid = opts?.glossMid ?? 0.12;
  const rimLighten = opts?.rimLighten ?? 22;

  const canvas = textures.createCanvas(key, size, size)!;
  const ctx = canvas.getContext();
  const cx = size / 2;
  const cy = size / 2;
  const rad = size / 2 - 6;

  // Subtle shadow beneath
  ctx.beginPath();
  ctx.arc(cx, cy + 3, rad + 2, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fill();

  // Main body — radial gradient with off-center highlight
  const body = ctx.createRadialGradient(cx - rad * 0.2, cy - rad * 0.25, rad * 0.08, cx, cy, rad);
  body.addColorStop(0, lighten(hex, lightenBody));
  body.addColorStop(0.45, rgba(hex));
  body.addColorStop(1, darken(hex, 28));
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fillStyle = body;
  ctx.fill();

  // Glossy highlight
  const gloss = ctx.createRadialGradient(cx - rad * 0.25, cy - rad * 0.3, 1, cx - rad * 0.1, cy - rad * 0.12, rad * 0.65);
  gloss.addColorStop(0, `rgba(255,255,255,${glossCenter})`);
  gloss.addColorStop(0.45, `rgba(255,255,255,${glossMid})`);
  gloss.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fillStyle = gloss;
  ctx.fill();

  // Rim
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.strokeStyle = lighten(hex, rimLighten);
  ctx.lineWidth = 2.4;
  ctx.stroke();

  canvas.refresh();
}

interface GlowHaloOpts {
  centerAlpha?: number;
  midAlpha?: number;
}

function drawGlowHalo(
  textures: Phaser.Textures.TextureManager,
  key: string,
  hex: number,
  opts?: GlowHaloOpts,
): void {
  const size = 160;
  const centerAlpha = opts?.centerAlpha ?? 0.45;
  const midAlpha = opts?.midAlpha ?? 0.18;

  const canvas = textures.createCanvas(key, size, size)!;
  const ctx = canvas.getContext();
  const cx = size / 2;
  const cy = size / 2;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  grad.addColorStop(0, rgba(hex, centerAlpha));
  grad.addColorStop(0.35, rgba(hex, midAlpha));
  grad.addColorStop(1, rgba(hex, 0));
  ctx.beginPath();
  ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  canvas.refresh();
}

function generateAttackerTextures(textures: Phaser.Textures.TextureManager): void {
  for (let i = 0; i < GAME_COLORS.length; i++) {
    drawAttackerOrb(textures, `attacker-${i}`, GAME_COLORS[i].hex);
  }
}

// ── Glow halo (rendered behind attacker) ─────────────────────────────────────

function generateGlowTextures(textures: Phaser.Textures.TextureManager): void {
  for (let i = 0; i < GAME_COLORS.length; i++) {
    drawGlowHalo(textures, `glow-${i}`, GAME_COLORS[i].hex);
  }
}

function generateMysteryAttackerTexture(textures: Phaser.Textures.TextureManager): void {
  drawAttackerOrb(textures, 'attacker-mystery', 0x555577, {
    lightenBody: 30, glossCenter: 0.45, glossMid: 0.08, rimLighten: 18,
  });
}

function generateMysteryGlowTexture(textures: Phaser.Textures.TextureManager): void {
  drawGlowHalo(textures, 'glow-mystery', 0x555577, { centerAlpha: 0.35, midAlpha: 0.12 });
}

// ── Projectile: bright orb with glow ─────────────────────────────────────────

function generateProjectileTextures(textures: Phaser.Textures.TextureManager): void {
  const size = 32;

  for (let i = 0; i < GAME_COLORS.length; i++) {
    const hex = GAME_COLORS[i].hex;
    const canvas = textures.createCanvas(`projectile-${i}`, size, size)!;
    const ctx = canvas.getContext();
    const cx = size / 2;
    const cy = size / 2;

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,0.95)');
    grad.addColorStop(0.25, lighten(hex, 45));
    grad.addColorStop(0.6, rgba(hex, 0.6));
    grad.addColorStop(1, rgba(hex, 0));
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    canvas.refresh();
  }
}

// ── Soft particle (white, tinted at runtime) ─────────────────────────────────

function generateParticleTexture(textures: Phaser.Textures.TextureManager): void {
  const size = 16;
  const canvas = textures.createCanvas('particle', size, size)!;
  const ctx = canvas.getContext();
  const cx = size / 2;
  const cy = size / 2;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.4, 'rgba(255,255,255,0.6)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  canvas.refresh();
}

// ── Conveyor flow dot ────────────────────────────────────────────────────────

function generateConveyorDot(textures: Phaser.Textures.TextureManager): void {
  const size = 16;
  const canvas = textures.createCanvas('conveyor-dot', size, size)!;
  const ctx = canvas.getContext();
  const cx = size / 2;
  const cy = size / 2;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  grad.addColorStop(0, 'rgba(180,180,255,0.9)');
  grad.addColorStop(0.4, 'rgba(120,120,200,0.5)');
  grad.addColorStop(1, 'rgba(100,100,180,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  canvas.refresh();
}

// ── Waiting slot glass container ─────────────────────────────────────────────

function generateSlotTexture(textures: Phaser.Textures.TextureManager): void {
  const size = 80;
  const r = 12;
  const canvas = textures.createCanvas('slot-bg', size, size)!;
  const ctx = canvas.getContext();

  // Glass background
  roundedRect(ctx, 2, 2, size - 4, size - 4, r);
  const grad = ctx.createLinearGradient(0, 0, 0, size);
  grad.addColorStop(0, 'rgba(60,60,100,0.6)');
  grad.addColorStop(1, 'rgba(30,30,60,0.4)');
  ctx.fillStyle = grad;
  ctx.fill();

  // Border
  roundedRect(ctx, 3, 3, size - 6, size - 6, r);
  ctx.strokeStyle = 'rgba(100,100,150,0.5)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Inner highlight
  ctx.save();
  roundedRect(ctx, 4, 4, size - 8, (size - 8) * 0.35, r - 2);
  ctx.clip();
  const hl = ctx.createLinearGradient(0, 4, 0, size * 0.3);
  hl.addColorStop(0, 'rgba(255,255,255,0.12)');
  hl.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = hl;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();

  canvas.refresh();
}

// ---------------------------------------------------------------------------
// Per-level palette texture regeneration
// ---------------------------------------------------------------------------

/**
 * Regenerate color-indexed textures (cube, snake-cube, attacker, glow, projectile)
 * for a custom gameplay palette. Call at level start when `gameplayPalette` is present.
 * Only regenerates indices 0..palette.length-1.
 */
export function regenerateColorTextures(
  textures: Phaser.Textures.TextureManager,
  palette: string[],
): void {
  for (let i = 0; i < palette.length; i++) {
    const hex = parseInt(palette[i].slice(1), 16);

    // Remove existing textures for this index
    for (const prefix of ['cube-', 'snake-cube-', 'attacker-', 'glow-', 'projectile-']) {
      const key = `${prefix}${i}`;
      if (textures.exists(key)) textures.remove(key);
    }

    // Regenerate cube
    {
      const size = 32;
      const r = 6;
      const canvas = textures.createCanvas(`cube-${i}`, size, size)!;
      const ctx = canvas.getContext();
      roundedRect(ctx, 2, 2, size - 4, size - 4, r);
      const grad = ctx.createLinearGradient(0, 0, 0, size);
      grad.addColorStop(0, lighten(hex, 30));
      grad.addColorStop(0.5, rgba(hex));
      grad.addColorStop(1, darken(hex, 20));
      ctx.fillStyle = grad;
      ctx.fill();
      drawBevel(ctx, hex, 2, 2, size - 4, size - 4, r);
      roundedRect(ctx, 3, 3, size - 6, size - 6, r);
      ctx.strokeStyle = lighten(hex, 20);
      ctx.lineWidth = 1.6;
      ctx.stroke();
      canvas.refresh();
    }

    // Regenerate snake-cube
    {
      const size = 32;
      const r = 6;
      const canvas = textures.createCanvas(`snake-cube-${i}`, size, size)!;
      const ctx = canvas.getContext();
      roundedRect(ctx, 2, 2, size - 4, size - 4, r);
      const grad = ctx.createLinearGradient(0, 0, 0, size);
      grad.addColorStop(0, lighten(hex, 30));
      grad.addColorStop(0.5, rgba(hex));
      grad.addColorStop(1, darken(hex, 20));
      ctx.fillStyle = grad;
      ctx.fill();
      drawBevel(ctx, hex, 2, 2, size - 4, size - 4, r);
      ctx.save();
      roundedRect(ctx, 2, 2, size - 4, size - 4, r);
      ctx.clip();
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 3;
      for (let d = -size; d < size * 2; d += 8) {
        ctx.beginPath();
        ctx.moveTo(d, 0);
        ctx.lineTo(d + size, size);
        ctx.stroke();
      }
      ctx.restore();
      roundedRect(ctx, 3, 3, size - 6, size - 6, r);
      ctx.strokeStyle = lighten(hex, 35);
      ctx.lineWidth = 2;
      ctx.stroke();
      canvas.refresh();
    }

    // Regenerate attacker orb
    drawAttackerOrb(textures, `attacker-${i}`, hex);

    // Regenerate glow
    drawGlowHalo(textures, `glow-${i}`, hex);

    // Regenerate projectile
    {
      const size = 32;
      const canvas = textures.createCanvas(`projectile-${i}`, size, size)!;
      const ctx = canvas.getContext();
      const cx = size / 2;
      const cy = size / 2;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
      grad.addColorStop(0, 'rgba(255,255,255,0.95)');
      grad.addColorStop(0.25, lighten(hex, 45));
      grad.addColorStop(0.6, rgba(hex, 0.6));
      grad.addColorStop(1, rgba(hex, 0));
      ctx.beginPath();
      ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
      canvas.refresh();
    }
  }
}
